/**
 * Every role there is, stored on `users.role`. See `src/access/README.md` for what each one grants.
 *
 * Listed in the order the admin select shows them, so each ladder reads as growing permission.
 * `api-read` trails because it is a machine leaf, not a rung above `admin`.
 */
export const ROLE = {
  USER: 'user',
  CONTENT_CREATOR: 'content-creator',
  CONTENT_EDITOR: 'content-editor',
  CONTENT_CURATOR: 'content-curator',
  DEV: 'developer',
  ADMIN: 'admin',
  API_READ: 'api-read',
} as const;
export type Role = (typeof ROLE)[keyof typeof ROLE];

/** A role this build no longer knows fails here, so a stale row reads as no role at all. */
export const isRole = (value: unknown): value is Role => typeof value === 'string' && Object.values<string>(ROLE).includes(value);

export const normalizeRoles = (roles: Role | Role[]): Role[] => (Array.isArray(roles) ? roles : [roles]);

export const RoleLabels: Record<Role, string> = {
  [ROLE.USER]: 'User',
  [ROLE.CONTENT_CREATOR]: 'Content Creator',
  [ROLE.CONTENT_EDITOR]: 'Content Editor',
  [ROLE.CONTENT_CURATOR]: 'Content Curator',
  [ROLE.DEV]: 'Developer',
  [ROLE.ADMIN]: 'Admin',
  [ROLE.API_READ]: 'API Read',
};

/**
 * Who inherits whom. Naming `user` in a gate means every landing account; naming `developer` means
 * Developer and above.
 *
 * The three empty entries are deliberate, not omissions. `user` is the only non-staff landing rung.
 * `content-creator` reaches no page, which is what keeps the two ladders apart. And `api-read` cannot
 * inherit `user`, which carries create and update, so gates name that leaf outright instead.
 */
export const RoleTree: Record<Role, Role[]> = {
  [ROLE.USER]: [],
  [ROLE.API_READ]: [],
  [ROLE.CONTENT_CREATOR]: [],
  [ROLE.CONTENT_EDITOR]: [ROLE.CONTENT_CREATOR],
  [ROLE.CONTENT_CURATOR]: [ROLE.CONTENT_EDITOR],
  [ROLE.DEV]: [ROLE.USER, ROLE.CONTENT_CURATOR],
  [ROLE.ADMIN]: [ROLE.DEV],
};
